import type { LogsEvent } from '@datadog/browser-logs';

/**
 * Removes the query string and the fragment from every URL carried by a Datadog log event.
 *
 * List screens keep what the user typed in the URL (the `filter` query parameter of API requests, the `pstate`
 * parameter of the page URL), so a failed or cancelled request, or any log emitted from such a page, would ship
 * patient data to Datadog. Method, host, path and status are kept so failures stay diagnosable.
 *
 * Kept out of the service so that it stays a pure, assertable function.
 */
export class LogEventSanitizerHelper {
  /** A network error message is `{Fetch|XHR} error {METHOD} {url}`: the URL runs to the end, spaces included. */
  private static readonly _networkErrorMessage = /^((?:Fetch|XHR) error [A-Z]+ \S*?)[?#].*$/s;

  /**
   * An absolute URL followed by a query and/or fragment.
   *
   * The URL part stops at whitespace, quotes, brackets, braces and commas, so a URL embedded in JSON or prose does not
   * swallow the text after it, and it also stops before the next `http(s)://` so that the scan stays linear on long
   * tokens. The suffix runs to the next whitespace, quote, or angle bracket (the query of an encoded URL contains none
   * of them), excluding a `:` followed by whitespace or the end, as in Angular's `... {url}: 0 undefined`.
   */
  private static readonly _urlWithSuffix = /(https?:\/\/(?:(?!https?:\/\/)[^\s?#"'<>()[\]{},])+)[?#](?:[^\s"'<>:]|:(?!\s|$))*/g;

  /** Sanitises the event in place. */
  public static sanitize(event: LogsEvent): void {
    if (event.http?.url) {
      event.http.url = LogEventSanitizerHelper.stripUrlQuery(event.http.url);
    }
    if (event.view?.url) {
      event.view.url = LogEventSanitizerHelper.stripUrlQuery(event.view.url);
    }
    if (event.view?.referrer) {
      event.view.referrer = LogEventSanitizerHelper.stripUrlQuery(event.view.referrer);
    }
    event.message = LogEventSanitizerHelper.stripUrlQueriesInText(event.message);

    if (event.error) {
      event.error.stack = LogEventSanitizerHelper._cleanOptional(event.error.stack);
      event.error.message = LogEventSanitizerHelper._cleanOptional(event.error.message);
      for (const cause of event.error.causes ?? []) {
        cause.message = LogEventSanitizerHelper.stripUrlQueriesInText(cause.message);
        cause.stack = LogEventSanitizerHelper._cleanOptional(cause.stack);
      }
    }
  }

  /** Removes the query string and the fragment of every URL found in free text, leaving the rest of the text intact. */
  public static stripUrlQueriesInText(text: string): string {
    if (!text) {
      return text;
    }
    const networkMatch = LogEventSanitizerHelper._networkErrorMessage.exec(text);
    if (networkMatch) {
      return networkMatch[1];
    }
    return text.replace(LogEventSanitizerHelper._urlWithSuffix, '$1');
  }

  /** Removes the query string and the fragment of a single URL. */
  public static stripUrlQuery(url: string): string {
    const index = url.search(/[?#]/);
    return index === -1 ? url : url.substring(0, index);
  }

  private static _cleanOptional(text: string | undefined): string | undefined {
    return text === undefined ? undefined : LogEventSanitizerHelper.stripUrlQueriesInText(text);
  }
}
